import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, Sparkle, Heart, Crown, ArrowRight, Quotes } from "@phosphor-icons/react";

const stagger = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-brand-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-28 relative z-10">
          <motion.div initial="hidden" animate="show" variants={stagger} className="max-w-4xl mx-auto text-center">
            <motion.div variants={fadeUp} className="label-eyebrow text-brand-terracotta">
              Bismillah · A concierge for the Muslim ummah
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="mt-8 font-serif text-[3.4rem] sm:text-[4.6rem] lg:text-[6.5rem] leading-[0.94] tracking-tighter text-brand-ink"
              data-testid="hero-title"
            >
              Nikah, <span className="italic text-brand-terracotta">with intention.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-8 mx-auto max-w-xl text-lg text-brand-muted leading-relaxed">
              WR Rishtey is a hand-curated matchmaking service for Muslim
              families who value deen, adab, and lasting companionship.
              No swiping. No noise. Only meaningful introductions.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-12 flex flex-wrap justify-center items-center gap-4">
              {user ? (
                <Button onClick={() => nav("/dashboard")} data-testid="hero-cta-dashboard" className="rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white px-8 py-6 text-base">
                  Enter Your Dashboard <ArrowRight size={18} className="ml-2" />
                </Button>
              ) : (
                <>
                  <Button onClick={() => nav("/register")} data-testid="hero-cta-register" className="rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white px-8 py-6 text-base">
                    Begin Your Profile <ArrowRight size={18} className="ml-2" />
                  </Button>
                  <Button variant="ghost" onClick={() => nav("/login")} data-testid="hero-cta-login" className="rounded-full text-brand-ink hover:text-brand-terracotta px-6 py-6">
                    Already a member? Sign in
                  </Button>
                </>
              )}
            </motion.div>
            <motion.div variants={fadeUp} className="mt-16 flex flex-wrap justify-center items-center gap-x-10 gap-y-6 border-t border-brand-line pt-10">
              <Stat n="48,200+" label="Verified profiles" />
              <div className="h-10 w-px bg-brand-line hidden sm:block" />
              <Stat n="6,900+" label="Nikahs solemnised" />
              <div className="h-10 w-px bg-brand-line hidden sm:block" />
              <Stat n="27" label="Countries served" />
            </motion.div>
          </motion.div>
        </div>
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-brand-gold/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full bg-brand-terracotta/10 blur-3xl pointer-events-none" />
      </section>

      {/* PILLARS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-3 gap-px bg-brand-line border border-brand-line">
          {[
            { icon: ShieldCheck, title: "Every profile is verified", body: "Government-ID and family reference checks run before a profile is visible to the community." },
            { icon: Sparkle, title: "Curated, not scrolled", body: "A human relationship manager assembles a discreet weekly shortlist — no endless swiping." },
            { icon: Heart, title: "Discreet & respectful", body: "Contact details reveal only after mutual consent. Your family's privacy is treated as sacred." },
          ].map((p, i) => (
            <div key={i} className="bg-brand-bone p-10 md:p-12">
              <p.icon size={30} weight="duotone" className="text-brand-terracotta" />
              <h3 className="font-serif text-3xl mt-6 text-brand-ink">{p.title}</h3>
              <p className="mt-4 text-brand-muted leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="label-eyebrow text-brand-terracotta">The process</div>
            <h2 className="font-serif text-5xl md:text-6xl mt-4 tracking-tighter text-brand-ink">
              Four graceful<br />steps.
            </h2>
            <p className="mt-6 text-brand-muted max-w-sm">
              Move at the pace of your family. Everything happens with your consent, and with utmost regard for adab.
            </p>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-6">
            {[
              ["01", "Build your dossier", "Share your background, deen, aspirations, and family — in one warm sitting."],
              ["02", "Meet your matchmaker", "A dedicated advisor curates a discreet weekly shortlist for you."],
              ["03", "Exchange interest", "Both families accept before any contact detail is revealed."],
              ["04", "Meet, with your wali", "We help coordinate meetings that feel effortless, respectful, and unhurried."],
            ].map(([n, t, b]) => (
              <div key={n} className="border border-brand-line p-8 bg-white hover:-translate-y-1 hover:shadow-ambient transition-transform duration-300">
                <div className="font-serif text-4xl text-brand-gold num-lining">{n}</div>
                <h4 className="font-serif text-2xl mt-4 text-brand-ink">{t}</h4>
                <p className="mt-3 text-sm text-brand-muted leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUCCESS STORY (text only) */}
      <section id="stories" className="max-w-[1200px] mx-auto px-6 md:px-10 py-24">
        <div className="text-center">
          <Quotes size={40} weight="fill" className="text-brand-terracotta mx-auto" />
          <p className="font-serif text-3xl md:text-5xl leading-tight text-brand-ink mt-6 italic max-w-3xl mx-auto">
            "The matchmaker understood our family in a way even our closest relatives could not. She listened first, and matched later — Alhamdulillah."
          </p>
          <div className="mt-10">
            <div className="label-eyebrow text-brand-muted">Featured story</div>
            <div className="mt-2 font-serif text-2xl text-brand-ink">Fatima &amp; Ahmed</div>
            <div className="text-brand-muted text-sm">Hyderabad → Toronto · Nikah, Autumn 2025</div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <div className="relative overflow-hidden bg-brand-ink text-white p-12 md:p-20">
          <div className="grid md:grid-cols-12 gap-8 items-center relative z-10">
            <div className="md:col-span-8">
              <Crown size={28} weight="duotone" className="text-brand-gold" />
              <h2 className="font-serif text-4xl md:text-6xl mt-4 tracking-tighter">
                Begin with a profile,<br />end with a nikah.
              </h2>
              <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
                Registration is complimentary. Upgrade only when you find a shortlist worth exploring, in-shaa-Allah.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Button
                onClick={() => nav(user ? "/dashboard" : "/register")}
                data-testid="footer-cta-register"
                className="rounded-full bg-brand-gold text-brand-ink hover:bg-brand-gold/90 px-8 py-6 text-base"
              >
                {user ? "Go to Dashboard" : "Create your free profile"} <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-terracotta/30 blur-3xl" />
        </div>
      </section>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-serif text-3xl text-brand-ink num-lining">{n}</div>
      <div className="text-xs uppercase tracking-widest text-brand-muted mt-1">{label}</div>
    </div>
  );
}
